import { useBeaconContext } from './BeaconConnectionWrapper';
import { BeaconPlayerWrapper } from './BeaconPlayerWrapper';
import { QuickstartLoading } from '../quickstart/components/QuickstartLoading';
import { QuickstartCard } from '../quickstart/components/QuickstartCard';
import { lazy, Suspense, useState, useCallback } from 'react';
import { BEACON_THEME } from './beaconTheme';
import { mediaStreamState } from '../../api/stores/playerStores';

const BeaconDebugOverlay = lazy(() =>
  import('./components/BeaconDebugOverlay').then((m) => ({ default: m.BeaconDebugOverlay })),
);
const PlayerDebugOverlay = lazy(() =>
  import('../../app/player/quickstart/QuickstartPlayerDebugOverlay').then((m) => ({ default: m.PlayerDebugOverlay })),
);

interface BeaconErrorCardProps {
  title: string;
  message: string;
  buttonLabel: string;
  onAction: () => void;
}

const PAGE_BACKGROUND = '#000000';
const BUTTON_YELLOW = '#FACC15';

const BeaconErrorCard = ({ title, message, buttonLabel, onAction }: BeaconErrorCardProps) => (
  <div
    className='flex min-h-screen items-center justify-center p-4'
    style={{ backgroundColor: PAGE_BACKGROUND }}
  >
    <QuickstartCard theme={BEACON_THEME} hideBadge>
      <div className='flex flex-col items-center gap-4 py-6 text-center'>
        <h1 className='text-xl font-semibold text-white'>{title}</h1>
        <p className='max-w-xs text-zinc-400'>{message}</p>
        <button
          onClick={onAction}
          className='mt-2 rounded-lg px-6 py-2.5 text-sm font-bold transition-opacity hover:opacity-90'
          style={{ backgroundColor: BUTTON_YELLOW, color: '#000' }}
        >
          {buttonLabel}
        </button>
      </div>
    </QuickstartCard>
  </div>
);

export const BeaconJoinPage = () => {
  const { state, coordinator, isDebugMode } = useBeaconContext();
  const [debugMobileGamepad, setDebugMobileGamepad] = useState(false);

  const handleDebugMediaStream = useCallback((videoStream: MediaStream | null, audioStream: MediaStream | null) => {
    mediaStreamState.updateState({
      stream: videoStream || new MediaStream(),
      audioStream: audioStream || new MediaStream(),
    });
  }, []);

  const debugOverlay = isDebugMode ? (
    <Suspense fallback={null}>
      <BeaconDebugOverlay
        currentPhase={state.phase}
        sessionId={state.sessionId}
        onPhaseChange={(phase) => coordinator?.transitionTo(phase)}
      />
    </Suspense>
  ) : null;

  const playerDebugOverlay = isDebugMode && state.phase === 'playing' ? (
    <Suspense fallback={null}>
      <PlayerDebugOverlay
        sessionId={state.sessionId}
        accentColor={BEACON_THEME.primaryColor}
        extraSessionRows={[{ label: 'Share Code', value: state.shareCode }]}
        mobileGamepadEnabled={debugMobileGamepad}
        onToggleMobileGamepad={() => setDebugMobileGamepad((prev) => !prev)}
        onDebugMediaStream={handleDebugMediaStream}
      />
    </Suspense>
  ) : null;

  const handleReload = () => window.location.reload();

  switch (state.phase) {
    case 'page_viewed':
    case 'fast_starting':
      return (
        <>
          <QuickstartLoading theme={BEACON_THEME} message='Connecting to host...' badgeText={state.streamTitle} />
          {debugOverlay}
        </>
      );

    case 'connecting_peer':
      return (
        <>
          <QuickstartLoading theme={BEACON_THEME} message='Establishing stream...' badgeText={state.streamTitle} />
          {debugOverlay}
        </>
      );

    case 'playing':
      return (
        <>
          <BeaconPlayerWrapper debugMobileGamepad={isDebugMode && debugMobileGamepad} />
          {playerDebugOverlay}
          {debugOverlay}
        </>
      );

    case 'not_found':
      return (
        <>
          <BeaconErrorCard
            title='Host Not Found'
            message='This link is no longer active. The host may have gone offline or the share code has expired.'
            buttonLabel='Go to Playcast'
            onAction={() => { window.location.href = 'https://playcast.io'; }}
          />
          {debugOverlay}
        </>
      );

    case 'kicked':
      return (
        <>
          <BeaconErrorCard
            title='Removed by Host'
            message='The host has ended your session.'
            buttonLabel='Back to Playcast'
            onAction={() => { window.location.href = 'https://playcast.io'; }}
          />
          {debugOverlay}
        </>
      );

    case 'disconnected':
      return (
        <>
          <BeaconErrorCard
            title='Disconnected'
            message='Your connection to the host was lost.'
            buttonLabel='Reconnect'
            onAction={handleReload}
          />
          {debugOverlay}
        </>
      );

    case 'error':
      return (
        <>
          <BeaconErrorCard
            title='Connection Error'
            message='Something went wrong while connecting to the host.'
            buttonLabel='Try Again'
            onAction={handleReload}
          />
          {debugOverlay}
        </>
      );

    default:
      return null;
  }
};
